import { useState, useEffect } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, Cell, ReferenceLine,
} from 'recharts';
import client from '../../api/client';

const SPORT_LABELS = {
  americanfootball_nfl: 'NFL', americanfootball_ncaaf: 'NCAAF',
  basketball_nba: 'NBA', basketball_ncaab: 'NCAAB',
  baseball_mlb: 'MLB', icehockey_nhl: 'NHL', soccer_usa_mls: 'MLS',
  mma_mixed_martial_arts: 'MMA', boxing_boxing: 'Boxing',
};

function StatCard({ label, value, sub, color }) {
  return (
    <div className="rounded-md border border-[#E5E7EB] dark:border-[#1e2536] bg-white dark:bg-[#151a25] px-3 py-2.5">
      <p className="text-[12px] uppercase tracking-[0.5px] text-[#6B7280] dark:text-[#8892a8] mb-1" style={{ fontFamily: 'var(--font-sans)' }}>{label}</p>
      <p className={`text-[22px] font-semibold ${color || 'text-[#111827] dark:text-[#e2e8f0]'}`} style={{ fontFamily: 'var(--font-sans)' }}>{value}</p>
      {sub && <p className="text-[12px] text-[#6B7280] dark:text-[#8892a8] mt-0.5" style={{ fontFamily: 'var(--font-sans)' }}>{sub}</p>}
    </div>
  );
}

function SectionHeader({ children }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <div className="w-[3px] h-4 bg-[#005BB3] rounded-full" />
      <h3 className="text-[12px] uppercase tracking-[0.5px] font-semibold text-[#6B7280] dark:text-[#8892a8]" style={{ fontFamily: 'var(--font-sans)' }}>{children}</h3>
    </div>
  );
}

function SmallSample({ count }) {
  if (count >= 10) return null;
  return <span className="text-[10px] text-[#F86A38] ml-1">({count} games — small sample)</span>;
}

export default function AccuracyDashboard() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [days, setDays] = useState(30);

  function fetchAll() {
    setLoading(true); setError(null);
    Promise.all([
      client.get(`/api/ai/prediction-accuracy?days=${days}`),
      client.get('/api/ai/prediction-accuracy/recent?limit=30'),
    ])
      .then(([s, r]) => {
        setStats(s.data);
        setRecent(Array.isArray(r.data) ? r.data : []);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }

  useEffect(() => { fetchAll(); }, [days]);

  const hasData = stats && stats.total > 0;

  return (
    <div className="px-5 py-6">
      {/* Header */}
      <div className="mb-1">
        <p className="text-[12px] uppercase tracking-[1px] font-medium text-[#005BB3] dark:text-[#3b82f6]" style={{ fontFamily: 'var(--font-sans)' }}>Admin</p>
      </div>
      <h1 className="text-[22px] font-semibold text-[#111827] dark:text-[#e2e8f0] tracking-tight mb-1" style={{ fontFamily: 'var(--font-sans)' }}>Prediction Accuracy</h1>
      <p className="text-[14px] text-[#6B7280] dark:text-[#8892a8] mb-5" style={{ fontFamily: 'var(--font-sans)' }}>How well do the AI predictions perform against actual game outcomes?</p>

      {/* Time range selector */}
      <div className="flex items-end gap-0 mb-5 border-b border-[#E5E7EB] dark:border-[#1e2536] pb-0">
        <span className="text-[11px] uppercase tracking-[1px] font-semibold text-[#9CA3AF] dark:text-[#505a6e] mr-3 pb-2 select-none" style={{ fontFamily: 'var(--font-sans)' }}>Period</span>
        {[7, 14, 30, 60, 90].map((d) => (
          <button key={d} onClick={() => setDays(d)}
            className={`px-0 mr-5 pb-2 text-[13px] whitespace-nowrap transition-colors duration-150 border-b-2 ${
              days === d
                ? 'font-semibold text-[#111827] dark:text-[#e2e8f0] border-[#005BB3] dark:border-[#3b82f6]'
                : 'font-medium text-[#6B7280] dark:text-[#8892a8] border-transparent hover:text-[#4B5563] dark:hover:text-[#e2e8f0]'
            }`}
            style={{ fontFamily: 'var(--font-sans)' }}>
            {d}d
          </button>
        ))}
      </div>

      {error && (
        <div className="rounded bg-[rgba(239,68,68,0.06)] dark:bg-[rgba(239,68,68,0.08)] border border-[#fecaca] dark:border-[#7f1d1d] px-3 py-2 mb-4">
          <p className="text-[#ef4444] text-[11px]">Failed to load: {error}</p>
          <button onClick={fetchAll} className="text-[10px] text-[#ef4444] underline mt-1">Retry</button>
        </div>
      )}

      {loading ? (
        <div className="space-y-2">{[...Array(4)].map((_, i) => <div key={i} className="h-16 rounded bg-[#F8F9FA] dark:bg-[#151a25] skeleton" />)}</div>
      ) : !hasData ? (
        <div className="max-w-[1100px]">
          <div className="rounded-md border border-[#E5E7EB] dark:border-[#1e2536] bg-white dark:bg-[#151a25] px-6 py-12 text-center">
            <p className="text-[16px] text-[#6B7280] dark:text-[#8892a8] mb-2" style={{ fontFamily: 'var(--font-sans)' }}>Predictions are being collected</p>
            <p className="text-[14px] text-[#9CA3AF] dark:text-[#505a6e]" style={{ fontFamily: 'var(--font-sans)' }}>Check back after games complete to see how the AI performed. Predictions are auto-generated daily and scored after final results come in.</p>
          </div>
        </div>
      ) : (
        <div className="max-w-[1100px]">
          {/* Overall stat cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mb-4">
            <StatCard label="Total Scored" value={stats.total} />
            <StatCard label="AI Accuracy" value={`${stats.accuracy}%`}
              color={stats.accuracy >= 55 ? 'text-[#029456]' : stats.accuracy >= 50 ? 'text-[#111827] dark:text-[#e2e8f0]' : 'text-[#DC2626]'}
              sub={`${stats.correct} of ${stats.total} correct`} />
            <StatCard label="Book Accuracy" value={`${stats.book_accuracy}%`}
              sub={`${stats.book_correct} of ${stats.total}`} />
            <StatCard label="AI vs Book"
              value={stats.ai_accuracy > stats.book_accuracy ? `+${(stats.ai_accuracy - stats.book_accuracy).toFixed(1)}%` : `${(stats.ai_accuracy - stats.book_accuracy).toFixed(1)}%`}
              color={stats.ai_accuracy > stats.book_accuracy ? 'text-[#029456]' : stats.ai_accuracy < stats.book_accuracy ? 'text-[#DC2626]' : 'text-[#6B7280]'}
              sub="AI edge over market" />
            <StatCard label="Value Bets" value={stats.value_bets}
              sub={stats.value_bets > 0 ? `${stats.value_hits} hit (${stats.value_accuracy}%)` : 'None flagged'} />
            <StatCard label="Value Win Rate" value={stats.value_bets > 0 ? `${stats.value_accuracy}%` : '--'}
              color={stats.value_accuracy >= 55 ? 'text-[#029456]' : stats.value_accuracy > 0 ? 'text-[#111827] dark:text-[#e2e8f0]' : ''} />
          </div>

          {/* Charts row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
            {/* Accuracy over time */}
            <div className="rounded-md border border-[#E5E7EB] dark:border-[#1e2536] bg-white dark:bg-[#151a25] px-4 py-3">
              <SectionHeader>Accuracy Over Time</SectionHeader>
              {stats.over_time?.length > 1 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={stats.over_time}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                    <XAxis dataKey="date" tick={{ fill: '#6B7280', fontSize: 10, fontFamily: 'var(--font-mono)' }} stroke="transparent"
                      tickFormatter={(v) => { const d = new Date(v + 'T00:00:00'); return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); }} />
                    <YAxis domain={[0, 100]} tick={{ fill: '#6B7280', fontSize: 10, fontFamily: 'var(--font-mono)' }} stroke="transparent"
                      tickFormatter={(v) => `${v}%`} />
                    <Tooltip contentStyle={{ backgroundColor: 'var(--tooltip-bg)', border: '1px solid var(--tooltip-border)', borderRadius: 4, color: 'var(--tooltip-text)', fontSize: 11, fontFamily: 'var(--font-mono)' }}
                      formatter={(v, name) => [`${v}%`, name === 'accuracy' ? 'Accuracy' : name]}
                      labelFormatter={(v) => new Date(v + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} />
                    <ReferenceLine y={50} stroke="#9CA3AF" strokeDasharray="3 3" />
                    <Line type="monotone" dataKey="accuracy" stroke="#005BB3" strokeWidth={2} dot={{ r: 3, fill: '#005BB3' }} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-[#6B7280] dark:text-[#8892a8] text-[13px] py-8 text-center">Need at least 2 days of data for a trend chart.</p>
              )}
            </div>

            {/* By sport */}
            <div className="rounded-md border border-[#E5E7EB] dark:border-[#1e2536] bg-white dark:bg-[#151a25] px-4 py-3">
              <SectionHeader>Accuracy by Sport</SectionHeader>
              {stats.by_sport?.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={stats.by_sport.map((s) => ({ ...s, sport: SPORT_LABELS[s.sport] || s.sport }))} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" horizontal={false} />
                    <XAxis type="number" domain={[0, 100]} tick={{ fill: '#6B7280', fontSize: 10, fontFamily: 'var(--font-mono)' }} stroke="transparent"
                      tickFormatter={(v) => `${v}%`} />
                    <YAxis type="category" dataKey="sport" tick={{ fill: '#6B7280', fontSize: 11, fontFamily: 'var(--font-sans)' }} stroke="transparent" width={55} />
                    <Tooltip contentStyle={{ backgroundColor: 'var(--tooltip-bg)', border: '1px solid var(--tooltip-border)', borderRadius: 4, color: 'var(--tooltip-text)', fontSize: 11, fontFamily: 'var(--font-mono)' }}
                      formatter={(v, _, { payload }) => [`${v}% (${payload.correct}/${payload.total})`, 'Accuracy']} />
                    <ReferenceLine x={50} stroke="#9CA3AF" strokeDasharray="3 3" />
                    <Bar dataKey="accuracy" radius={[0, 4, 4, 0]}>
                      {stats.by_sport.map((s, i) => (
                        <Cell key={i} fill={s.accuracy >= 55 ? '#029456' : s.accuracy >= 50 ? '#005BB3' : '#DC2626'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-[#6B7280] dark:text-[#8892a8] text-[13px] py-8 text-center">No sport data yet.</p>
              )}
            </div>
          </div>

          {/* Calibration + Value bets row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
            {/* Calibration */}
            <div className="rounded-md border border-[#E5E7EB] dark:border-[#1e2536] bg-white dark:bg-[#151a25] px-4 py-3">
              <SectionHeader>Calibration by Confidence</SectionHeader>
              <p className="text-[12px] text-[#6B7280] dark:text-[#8892a8] mb-3" style={{ fontFamily: 'var(--font-sans)' }}>
                When the AI says a team has a 70% chance, do they win ~70% of the time?
              </p>
              {stats.calibration?.length > 0 ? (
                <div className="space-y-2">
                  {stats.calibration.map((b) => (
                    <div key={b.bucket} className="flex items-center gap-3">
                      <span className="text-[13px] text-[#111827] dark:text-[#e2e8f0] w-[70px] flex-shrink-0" style={{ fontFamily: 'var(--font-mono)' }}>{b.bucket}</span>
                      <div className="flex-1 h-5 rounded bg-[#E5E7EB] dark:bg-[#1a2030] overflow-hidden relative">
                        <div className={`h-full rounded ${b.accuracy >= 55 ? 'bg-[#029456]' : b.accuracy >= 50 ? 'bg-[#005BB3]' : 'bg-[#DC2626]'}`}
                          style={{ width: `${Math.min(b.accuracy, 100)}%` }} />
                        {/* Expected line */}
                        <div className="absolute top-0 h-full border-l-2 border-dashed border-[#6B7280]" style={{ left: `${b.avg_confidence}%` }} />
                      </div>
                      <span className="text-[12px] text-[#111827] dark:text-[#e2e8f0] w-[45px] text-right flex-shrink-0" style={{ fontFamily: 'var(--font-mono)' }}>
                        {b.accuracy}%
                      </span>
                      <span className="text-[11px] text-[#6B7280] dark:text-[#8892a8] w-[55px] flex-shrink-0" style={{ fontFamily: 'var(--font-mono)' }}>
                        {b.correct}/{b.total}
                      </span>
                    </div>
                  ))}
                  <p className="text-[10px] text-[#9CA3AF] dark:text-[#505a6e] mt-1">Dashed line = average AI confidence for that bucket. Bar = actual win rate.</p>
                </div>
              ) : (
                <p className="text-[#6B7280] dark:text-[#8892a8] text-[13px] py-4 text-center">Not enough data yet.</p>
              )}
            </div>

            {/* AI vs Bookmakers */}
            <div className="rounded-md border border-[#E5E7EB] dark:border-[#1e2536] bg-white dark:bg-[#151a25] px-4 py-3">
              <SectionHeader>AI vs Bookmakers</SectionHeader>
              <p className="text-[12px] text-[#6B7280] dark:text-[#8892a8] mb-3" style={{ fontFamily: 'var(--font-sans)' }}>
                Does the AI add value over simply following the market favorite?
              </p>
              <div className="space-y-3">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[13px] text-[#111827] dark:text-[#e2e8f0]" style={{ fontFamily: 'var(--font-sans)' }}>AI Predictions</span>
                    <span className="text-[13px] font-semibold text-[#005BB3] dark:text-[#3b82f6]" style={{ fontFamily: 'var(--font-mono)' }}>{stats.ai_accuracy}%</span>
                  </div>
                  <div className="h-4 rounded bg-[#E5E7EB] dark:bg-[#1a2030] overflow-hidden">
                    <div className="h-full rounded bg-[#005BB3]" style={{ width: `${Math.min(stats.ai_accuracy, 100)}%` }} />
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[13px] text-[#111827] dark:text-[#e2e8f0]" style={{ fontFamily: 'var(--font-sans)' }}>Bookmaker Implied</span>
                    <span className="text-[13px] font-semibold text-[#6B7280] dark:text-[#8892a8]" style={{ fontFamily: 'var(--font-mono)' }}>{stats.book_accuracy}%</span>
                  </div>
                  <div className="h-4 rounded bg-[#E5E7EB] dark:bg-[#1a2030] overflow-hidden">
                    <div className="h-full rounded bg-[#6B7280]" style={{ width: `${Math.min(stats.book_accuracy, 100)}%` }} />
                  </div>
                </div>
                <div className="pt-2 border-t border-[#E5E7EB] dark:border-[#1e2536]">
                  <p className="text-[13px]" style={{ fontFamily: 'var(--font-sans)' }}>
                    <span className="text-[#6B7280] dark:text-[#8892a8]">Difference: </span>
                    <span className={`font-semibold ${stats.ai_accuracy > stats.book_accuracy ? 'text-[#029456]' : stats.ai_accuracy < stats.book_accuracy ? 'text-[#DC2626]' : 'text-[#6B7280] dark:text-[#8892a8]'}`} style={{ fontFamily: 'var(--font-mono)' }}>
                      {stats.ai_accuracy > stats.book_accuracy ? '+' : ''}{(stats.ai_accuracy - stats.book_accuracy).toFixed(1)}%
                    </span>
                    <span className="text-[#6B7280] dark:text-[#8892a8]"> ({stats.ai_accuracy > stats.book_accuracy ? 'AI outperforming' : stats.ai_accuracy < stats.book_accuracy ? 'Market outperforming' : 'Even'})</span>
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Recent predictions table */}
          {recent.length > 0 && (
            <div className="rounded-md border border-[#E5E7EB] dark:border-[#1e2536] bg-white dark:bg-[#151a25] overflow-x-auto">
              <div className="flex items-center justify-between px-3 py-2 border-b border-[#E5E7EB] dark:border-[#1e2536]">
                <SectionHeader>Recent Predictions</SectionHeader>
              </div>
              <table className="w-full text-left min-w-[700px]">
                <thead>
                  <tr className="text-[12px] uppercase tracking-[0.5px] text-[#6B7280] dark:text-[#505a6e] border-b border-[#E5E7EB] dark:border-[#1e2536]">
                    <th className="py-2 px-3 font-semibold" style={{ fontFamily: 'var(--font-sans)' }}>Game</th>
                    <th className="py-2 px-2 font-semibold" style={{ fontFamily: 'var(--font-sans)' }}>Sport</th>
                    <th className="py-2 px-2 font-semibold" style={{ fontFamily: 'var(--font-sans)' }}>AI Pick</th>
                    <th className="py-2 px-2 font-semibold" style={{ fontFamily: 'var(--font-sans)' }}>Confidence</th>
                    <th className="py-2 px-2 font-semibold" style={{ fontFamily: 'var(--font-sans)' }}>Score</th>
                    <th className="py-2 px-2 font-semibold" style={{ fontFamily: 'var(--font-sans)' }}>Result</th>
                    <th className="py-2 px-2 font-semibold" style={{ fontFamily: 'var(--font-sans)' }}>Value</th>
                  </tr>
                </thead>
                <tbody>
                  {recent.map((r, i) => {
                    const aiFavored = r.home_win_pct >= 50 ? 'home' : 'away';
                    const aiTeam = aiFavored === 'home' ? r.home_team : r.away_team;
                    const aiPct = Math.max(r.home_win_pct, r.away_win_pct);
                    const correct = r.favored_won;
                    const sportLabel = SPORT_LABELS[r.sport_key] || r.sport_key;

                    return (
                      <tr key={r.event_id || i} className="border-b border-[#E5E7EB] dark:border-[#1e2536] text-[13px]">
                        <td className="py-2 px-3 text-[#111827] dark:text-[#e2e8f0] truncate max-w-[180px]" style={{ fontFamily: 'var(--font-sans)' }}>
                          {r.away_team} @ {r.home_team}
                        </td>
                        <td className="py-2 px-2 text-[#6B7280] dark:text-[#8892a8]" style={{ fontFamily: 'var(--font-sans)' }}>{sportLabel}</td>
                        <td className="py-2 px-2 text-[#111827] dark:text-[#e2e8f0]" style={{ fontFamily: 'var(--font-sans)' }}>
                          {aiTeam}
                          <span className="text-[#6B7280] dark:text-[#8892a8] ml-1" style={{ fontFamily: 'var(--font-mono)' }}>{aiPct.toFixed(0)}%</span>
                        </td>
                        <td className="py-2 px-2">
                          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                            r.confidence === 'high' ? 'bg-[rgba(34,197,94,0.1)] text-[#22c55e]'
                            : r.confidence === 'moderate' ? 'bg-[rgba(245,158,11,0.1)] text-[#f59e0b]'
                            : 'bg-[rgba(148,163,184,0.1)] text-[#94a3b8]'
                          }`}>{(r.confidence || 'low').toUpperCase()}</span>
                        </td>
                        <td className="py-2 px-2 text-[#111827] dark:text-[#e2e8f0]" style={{ fontFamily: 'var(--font-mono)' }}>
                          {r.home_score != null ? `${r.home_score}-${r.away_score}` : '--'}
                        </td>
                        <td className="py-2 px-2">
                          {correct === true && <span className="text-[#029456] font-semibold" style={{ fontFamily: 'var(--font-mono)' }}>CORRECT</span>}
                          {correct === false && <span className="text-[#DC2626] font-semibold" style={{ fontFamily: 'var(--font-mono)' }}>WRONG</span>}
                          {correct == null && <span className="text-[#6B7280] dark:text-[#8892a8]">--</span>}
                        </td>
                        <td className="py-2 px-2">
                          {r.value_side ? (
                            r.value_bet_hit === true ? <span className="text-[#029456] font-semibold" style={{ fontFamily: 'var(--font-mono)' }}>HIT</span>
                            : r.value_bet_hit === false ? <span className="text-[#DC2626] font-semibold" style={{ fontFamily: 'var(--font-mono)' }}>MISS</span>
                            : <span className="text-[#6B7280] dark:text-[#8892a8]">--</span>
                          ) : <span className="text-[#9CA3AF] dark:text-[#505a6e]">—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Sport sample size notes */}
          {stats.by_sport?.some((s) => s.total < 10) && (
            <p className="text-[11px] text-[#F86A38] mt-3" style={{ fontFamily: 'var(--font-sans)' }}>
              Note: Some sports have fewer than 10 scored predictions — treat those accuracy numbers as preliminary.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
